import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls = "rounded-xl border border-forest/15 bg-sand/50 px-4 py-2.5 text-sm outline-none focus:border-gold w-full";

const Field = ({ label, value, onChange, rows, testid }) => (
  <label className="flex flex-col gap-1.5">
    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">{label}</span>
    {rows ? (
      <textarea rows={rows} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y`} />
    ) : (
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    )}
  </label>
);

const Section = ({ title, hint, children, wide }) => (
  <div className="bg-white rounded-2xl border border-forest/10 p-6">
    <h3 className="font-serif text-xl font-semibold text-forest">{title}</h3>
    {hint && <p className="mt-1 text-xs text-ink/50">{hint}</p>}
    <div className={`mt-5 grid grid-cols-1 ${wide ? "" : "md:grid-cols-2"} gap-4`}>{children}</div>
  </div>
);

export default function AdminContent() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setC(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!c) return <p className="mt-6 text-ink/50" data-testid="content-loading">Inhalte werden geladen …</p>;

  const set = (key, value) => setC({ ...c, [key]: value });
  const setSpot = (i, field, value) => {
    const spots = c.region_spots.map((s, idx) => (idx === i ? { ...s, [field]: value } : s));
    set("region_spots", spots);
  };
  const setFaq = (i, field, value) => {
    set("faq_items", c.faq_items.map((f, idx) => (idx === i ? { ...f, [field]: value } : f)));
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/content", c);
      setC(data);
      toast.success("Alle Inhalte gespeichert — sofort auf der Website sichtbar");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 space-y-6" data-testid="admin-content-editor">
      <div className="flex items-center justify-between bg-forest-dark text-sand rounded-2xl px-6 py-4">
        <p className="text-sm">Ändern Sie hier alle Texte & Informationen Ihrer Website — ganz ohne Code.</p>
        <button onClick={save} disabled={busy} data-testid="content-save-button"
          className="flex items-center gap-2 rounded-full bg-gold text-forest-dark px-6 py-2.5 text-sm font-bold hover:bg-sand disabled:opacity-50 transition-colors">
          <Save size={15} /> {busy ? "Speichern …" : "Alles speichern"}
        </button>
      </div>

      <Section title="Kontakt & Adresse" hint="Erscheint im Footer und auf der gesamten Website.">
        <Field label="Straße & Nr." testid="content-field-address-1" value={c.contact_address_1} onChange={(v) => set("contact_address_1", v)} />
        <Field label="PLZ & Ort" testid="content-field-address-2" value={c.contact_address_2} onChange={(v) => set("contact_address_2", v)} />
        <Field label="Telefon (Anzeige)" testid="content-field-phone" value={c.contact_phone} onChange={(v) => set("contact_phone", v)} />
        <Field label="Telefon (Link, z. B. +49…)" testid="content-field-phone-link" value={c.contact_phone_link} onChange={(v) => set("contact_phone_link", v)} />
        <Field label="E-Mail" testid="content-field-email" value={c.contact_email} onChange={(v) => set("contact_email", v)} />
      </Section>

      <Section title="Startseite — großer Titel (Hero)">
        <Field label="Zeile über dem Titel" testid="content-field-hero-overline" value={c.hero_overline} onChange={(v) => set("hero_overline", v)} />
        <Field label="Titel Zeile 1" testid="content-field-hero-title-1" value={c.hero_title_1} onChange={(v) => set("hero_title_1", v)} />
        <Field label="Titel Zeile 2 (kursiv, gold)" testid="content-field-hero-title-2" value={c.hero_title_2} onChange={(v) => set("hero_title_2", v)} />
        <Field label="Einleitungstext" testid="content-field-hero-subtitle" rows={3} value={c.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} />
        <Field label="Hero-Bild (Pfad oder URL, z. B. /images/restaurant.jpg)" testid="content-field-hero-image" value={c.hero_image} onChange={(v) => set("hero_image", v)} />
      </Section>

      <Section title="Laufband (Marquee)" hint="Einträge mit Komma trennen." wide>
        <Field label="Einträge" testid="content-field-marquee" value={c.marquee_items.join(", ")} onChange={(v) => set("marquee_items", v.split(",").map((s) => s.trim()).filter(Boolean))} />
      </Section>

      <Section title="Über das Hotel (Kapitel I)">
        <Field label="Kapitel-Zeile" testid="content-field-manifesto-overline" value={c.manifesto_overline} onChange={(v) => set("manifesto_overline", v)} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Titel Teil 1" testid="content-field-manifesto-title-1" value={c.manifesto_title_1} onChange={(v) => set("manifesto_title_1", v)} />
          <Field label="Titel Teil 2 (kursiv)" testid="content-field-manifesto-title-2" value={c.manifesto_title_2} onChange={(v) => set("manifesto_title_2", v)} />
        </div>
        <Field label="Absatz 1" testid="content-field-manifesto-text-1" rows={4} value={c.manifesto_text_1} onChange={(v) => set("manifesto_text_1", v)} />
        <Field label="Absatz 2" testid="content-field-manifesto-text-2" rows={4} value={c.manifesto_text_2} onChange={(v) => set("manifesto_text_2", v)} />
        <Field label="Zitat auf dem Bild" testid="content-field-manifesto-quote" value={c.manifesto_quote} onChange={(v) => set("manifesto_quote", v)} />
        <Field label="Bild (Pfad oder URL, z. B. /images/hero.jpg)" testid="content-field-manifesto-image" value={c.manifesto_image} onChange={(v) => set("manifesto_image", v)} />
      </Section>

      <Section title="Zimmer-Bereich (Kapitel II)">
        <Field label="Kapitel-Zeile" testid="content-field-rooms-overline" value={c.rooms_overline} onChange={(v) => set("rooms_overline", v)} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Titel Teil 1" testid="content-field-rooms-title-1" value={c.rooms_title_1} onChange={(v) => set("rooms_title_1", v)} />
          <Field label="Titel Teil 2 (kursiv)" testid="content-field-rooms-title-2" value={c.rooms_title_2} onChange={(v) => set("rooms_title_2", v)} />
        </div>
        <Field label="Einleitungstext" testid="content-field-rooms-subline" rows={2} value={c.rooms_subline} onChange={(v) => set("rooms_subline", v)} />
      </Section>

      <Section title="Region (Kapitel III)" wide>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Kapitel-Zeile" testid="content-field-region-overline" value={c.region_overline} onChange={(v) => set("region_overline", v)} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Titel Teil 1" testid="content-field-region-title-1" value={c.region_title_1} onChange={(v) => set("region_title_1", v)} />
            <Field label="Titel Teil 2 (kursiv)" testid="content-field-region-title-2" value={c.region_title_2} onChange={(v) => set("region_title_2", v)} />
          </div>
        </div>
        {c.region_spots.map((s, i) => (
          <div key={i} className="rounded-xl border border-forest/10 bg-cream/50 p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`region-spot-editor-${i}`}>
            <Field label={`Karte ${i + 1} — Titel`} testid={`region-spot-title-${i}`} value={s.title} onChange={(v) => setSpot(i, "title", v)} />
            <Field label="Kleines Label" testid={`region-spot-tag-${i}`} value={s.tag} onChange={(v) => setSpot(i, "tag", v)} />
            <Field label="Bild (Pfad oder URL)" testid={`region-spot-img-${i}`} value={s.img} onChange={(v) => setSpot(i, "img", v)} />
            <Field label="Text" testid={`region-spot-text-${i}`} rows={2} value={s.text} onChange={(v) => setSpot(i, "text", v)} />
          </div>
        ))}
      </Section>

      <Section title="Häufige Fragen (FAQ)" wide>
        {c.faq_items.map((f, i) => (
          <div key={i} className="rounded-xl border border-forest/10 bg-cream/50 p-4 flex gap-3 items-start" data-testid={`faq-editor-${i}`}>
            <div className="flex-1 grid grid-cols-1 gap-3">
              <Field label={`Frage ${i + 1}`} testid={`faq-q-${i}`} value={f.q} onChange={(v) => setFaq(i, "q", v)} />
              <Field label="Antwort" testid={`faq-a-${i}`} rows={2} value={f.a} onChange={(v) => setFaq(i, "a", v)} />
            </div>
            <button onClick={() => set("faq_items", c.faq_items.filter((_, idx) => idx !== i))} data-testid={`faq-remove-${i}`}
              className="mt-6 rounded-full border border-destructive/25 p-2 text-destructive hover:bg-destructive hover:text-white transition-colors">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button onClick={() => set("faq_items", [...c.faq_items, { q: "Neue Frage", a: "Antwort …" }])} data-testid="faq-add-button"
          className="flex items-center gap-2 rounded-full border border-forest/20 px-5 py-2.5 text-sm font-semibold text-forest hover:bg-forest hover:text-sand transition-colors">
          <Plus size={15} /> Frage hinzufügen
        </button>
      </Section>

      <Section title="Buchung & Bankverbindung" hint="Wird Gästen nach einer Buchung per Überweisung angezeigt.">
        <Field label="Empfänger" testid="content-field-bank-recipient" value={c.bank_recipient} onChange={(v) => set("bank_recipient", v)} />
        <Field label="IBAN" testid="content-field-bank-iban" value={c.bank_iban} onChange={(v) => set("bank_iban", v)} />
        <Field label="BIC (optional)" testid="content-field-bank-bic" value={c.bank_bic} onChange={(v) => set("bank_bic", v)} />
      </Section>

      <Section title="Footer & Service" wide>
        <Field label="Kurztext über das Hotel" testid="content-field-footer-about" rows={2} value={c.footer_about} onChange={(v) => set("footer_about", v)} />
        <Field label="Service-Zeilen (eine pro Zeile)" testid="content-field-service-lines" rows={3} value={c.service_lines.join("\n")} onChange={(v) => set("service_lines", v.split("\n").filter((s) => s.trim()))} />
      </Section>

      <Section title="Rechnungen" hint="Diese Angaben erscheinen auf jeder Rechnung (Rechnungs-Button bei jeder Buchung).">
        <Field label="Firma / Rechnungssteller" testid="content-field-invoice-company" value={c.invoice_company} onChange={(v) => set("invoice_company", v)} />
        <Field label="Steuernummer / USt-IdNr." testid="content-field-invoice-tax-id" value={c.invoice_tax_id} onChange={(v) => set("invoice_tax_id", v)} />
        <Field label="Hinweistext am Ende der Rechnung" testid="content-field-invoice-note" rows={2} value={c.invoice_note} onChange={(v) => set("invoice_note", v)} />
      </Section>

      <Section title="Rechtstexte" hint="Überschriften mit ## am Zeilenanfang, z. B. ## Kontakt. Leere Zeile = neuer Absatz." wide>
        <Field label="Impressum" testid="content-field-impressum" rows={12} value={c.impressum_text} onChange={(v) => set("impressum_text", v)} />
        <Field label="Datenschutzerklärung" testid="content-field-datenschutz" rows={12} value={c.datenschutz_text} onChange={(v) => set("datenschutz_text", v)} />
        <Field label="AGB" testid="content-field-agb" rows={12} value={c.agb_text} onChange={(v) => set("agb_text", v)} />
      </Section>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={busy} data-testid="content-save-button-bottom"
          className="flex items-center gap-2 rounded-full bg-forest text-sand px-8 py-3.5 text-sm font-bold shadow-xl hover:bg-forest-dark disabled:opacity-50 transition-colors">
          <Save size={15} /> {busy ? "Speichern …" : "Alles speichern"}
        </button>
      </div>
    </div>
  );
}
